import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";

import {
    Alert as MuiAlert,
} from "@mui/material";
import ConfirmationDialog from "../../../../components/ConfirmationDialog";

// Types
import {dynamicListView} from "../../../../types/dynamicListView";

// Graphql
import {
    ImportsListQuery,
    useDeleteImportMutation,
    useImportsListQuery
} from "../../../../graphql/settings/import/import.graphql-gen";


// Style
import styled from "styled-components/macro";
import {spacing} from "@mui/system";
import {Filters} from "../../../../types/filters";
import Loader from "../../../../components/Loader";
import SnackbarNotification from "../../../../components/SnackbarNotification";
import {ApolloQueryResult} from "@apollo/client";


const Alert = styled(MuiAlert)(spacing);


export const ImportsContext = React.createContext<dynamicListView>({
    items: [],
    headers: [],
    count: 0,
    addItem: () => {
    },
    removeItem: (id: string) => {
    },
    selectItem: (id: string | number) => {
    },
    onPaginate: (limit: number, page: number) => {
    },
    onSearch: (searchText: string) => {
    },
    onSort: (filed: string, order: string) => {
    },
    page: 0,
    rowsPerPage: 10,
    search: '',
    sorting: {field: '', order: ''},
    filters: []
});

const ImportsContextProvider: React.FC = (props) => {
    const model: string = 'Import';

    // States
    const [items, setItems] = useState<any[]>([]);
    const [headers, setHeaders] = useState<any[]>([]);
    const [count, setCount] = useState<number>(0);
    const navigate = useNavigate();
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [id, setId] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');
    const [sorting, setSorting] = useState<{ field: string, order: string }>({field: 'updated_at', order: 'desc'});
    const [filters, setFilters] = useState<Filters[]>([]);

    // Get Imports data from API
    const {data, error, loading, refetch: refetchListViewData} = useImportsListQuery({
        variables: {
            model,
            //search: search,
            pagination: {
                limit: itemsPerPage,
                page: currentPage
            },
            // sorting: {
            //     field: sorting.field,
            //     order: sorting.order
            // }
        }
    });


    useEffect(() => {
        if (data) {
            setHeaders(data.getDynamicTable.tableHeader);
            setItems(data.getDynamicTable.tableData);
            setCurrentPage(data.getDynamicTable.page);
            setItemsPerPage(data.getDynamicTable.limit);
            setCount(data.getDynamicTable.count);
            setSearch(data.getDynamicTable.search);
            setFilters(data.getDynamicTable.filters);
            setSorting(data.getDynamicTable.sorting);
        }
    }, [data]);
    const [deleteImport] = useDeleteImportMutation();

    if (loading) {
        return <Loader/>;
    }

    // Handle error
    if (error) {
        return (
            <Alert mb={4} severity="error">
                Error!
            </Alert>
        );
    }


    //------------------------------- Public methods -------------------------------------


    // Handle add
    const addItemHandler = () => {
        navigate(`/settings/imports/new`, {replace: true});
    };

    // Handel delete
    const removeItemHandler = (id: string) => {
        if (id) {
            setId(id);
        }
        setOpen(true);

    }

    // Go to singe view
    const selectItemHandler = (id: string | number) => {
        navigate(`/settings/imports/${id}`, {replace: true});
    };

    // Delete mutation
    const deleteImportMutation = (id: string) => {
        setOpen(false);

        deleteImport({
            variables: {
                id
            }
        }).then((res: any) => {
            refetchListViewData({
                model,
                search,
                pagination: {
                    limit: itemsPerPage,
                    page: currentPage
                },
                sorting: {
                    field: sorting.field,
                    order: sorting.order
                }
            }).then((res:ApolloQueryResult<ImportsListQuery>) => {
                if (res) {
                    setHeaders(res.data.getDynamicTable.tableHeader);
                    setItems(res.data.getDynamicTable.tableData);
                    setCurrentPage(res.data.getDynamicTable.page);
                    setItemsPerPage(res.data.getDynamicTable.limit);
                    setCount(res.data.getDynamicTable.count);
                    setSearch(res.data.getDynamicTable.search);
                    setFilters(res.data.getDynamicTable.filters);
                    setSorting(res.data.getDynamicTable.sorting);
                }
            });
        }).catch(error => {
            return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
        });
    };

    // Paginate listview table
    const onPaginateHandler = (limit: number, page: number) => {
        refetchListViewData({
            model,
            search,
            pagination: {
                limit: limit,
                page: page
            },
            sorting: {
                field: sorting.field,
                order: sorting.order
            }
        }).catch(error => {
            return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
        });

        setItemsPerPage(limit);
        setCurrentPage(page);
    };


    // Search
    const onSearchHandler = (searchText: string) => {
        setSearch(searchText);

        refetchListViewData({
            model,
            search: searchText,
            pagination: {
                limit: itemsPerPage,
                page: currentPage
            },
            sorting: {
                field: sorting.field,
                order: sorting.order
            }
        }).catch(error => {
            return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
        });
    };

    // Sort
    const onSortHandler = (field: string, order: string) => {
        setCurrentPage(0);
        setSorting({field, order});

        refetchListViewData({
            model,
            search,
            pagination: {
                limit: itemsPerPage,
                page: 0
            },
            sorting: {
                field,
                order
            }
        }).catch(error => {
            return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
        });
    };

    // Close snackbar notification
    const closeDialog = () => {
        setOpen(false);
    }

    // Set context values
    const contextValue: dynamicListView = {
        items: items,
        headers: headers,
        count: count,
        addItem: addItemHandler,
        removeItem: removeItemHandler,
        selectItem: selectItemHandler,
        onPaginate: onPaginateHandler,
        onSearch: onSearchHandler,
        onSort: onSortHandler,
        page: currentPage,
        rowsPerPage: itemsPerPage,
        search: search,
        sorting: sorting,
        filters: filters,
    };

    return (
        <ImportsContext.Provider value={contextValue}>
            {props.children}

            <ConfirmationDialog
                message="Are you sure you want to remove the item? This action cannot be undone!"
                title="Delete"
                button="Delete"
                open={open}
                onConfirm={() => deleteImportMutation(id)}
                onCancel={closeDialog}/>
        </ImportsContext.Provider>
    );
};

export default ImportsContextProvider;
